import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useRef,
  useEffect,
} from "react";
import PropTypes from "prop-types";
import ReactQuill, { Quill } from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import "./index.scss";
import getStyled from "../../hooks/getStyled";
import InputLabel from "@mui/material/InputLabel";

const { styled } = getStyled();

import { EmojiBlot, ToolbarEmoji } from "quill2-emoji";
import "quill2-emoji/dist/style.css";

Quill.register(
  {
    "formats/emoji": EmojiBlot,
    "modules/emoji-toolbar": ToolbarEmoji,
  },
  true
);

const MaxLengthLabel = styled("div", { shouldForwardProp: false })(
  ({ theme }) => ({
    color: theme.palette.blue200.main,
    fontSize: 11,
    position: "absolute",
    right: 0,
  })
);

const SFTextEditor = forwardRef(
  (
    {
      value,
      onChange,
      label,
      readOnly = false,
      required = true,
      height = 100,
      toolbar = [["bold", "italic", { list: "ordered" }, { list: "bullet" }]],
      hasError = false,
      maxLength = null,
      customIcon = null,
      isCopilotField = false,
      preventSyncEffect = true,
    },
    ref
  ) => {
    const [_value, _setValue] = useState(value);

    const [maxLengthVisible, setMaxLengthVisible] = useState(false);

    const MaxLengthValue = (length, maxLength) => (
      <MaxLengthLabel>
        {length}/{maxLength}
      </MaxLengthLabel>
    );

    const [focus, setFocus] = useState(false);
    const editorRef = useRef();
    const modules = {
      toolbar: { container: toolbar },
      clipboard: {
        matchVisual: false,
      },
    };

    let hasEmoji = false;
    if (
      toolbar.find((a) => a == "emoji") ||
      toolbar[0].find((a) => a == "emoji")
    ) {
      hasEmoji = true;
    }

    if (hasEmoji) {
      modules["emoji-toolbar"] = true;
    }

    const theme = "snow";
    const formats = [
      "bold",
      "italic",
      "list",
      "bullet",
      ...(hasEmoji ? ["emoji"] : []),
    ];

    useEffect(() => {
      //this check was added because the editor when you delete any content inside is left with a value of
      //"<p><br></p>" therefore is not considered empty and the required validation is not triggered
      //this is a bug in the editor and the property clipboard: { matchVisual: false } is supposed to fix it
      //but it is deprecated as declared in the package's github issues
      onChange(_value === "<p><br></p>" ? undefined : _value);
    }, [_value]);

    useEffect(() => {
      // Effect to update the value when the value prop changes (i.e. when the form is reset or when the value is updated from the parent component)
      // TODO: Quill is an uncontrolled component but we're using it as a controlled component through JsonForms state management
      // This is a hacky workaround to force the value to update when the value prop changes from the parent component but it's not the best solution
      // We should consider choosing a different rich text editor that is more compatible with controlled components
      if (value !== _value && !preventSyncEffect) {
        _setValue(value);
      }
    }, [value, preventSyncEffect]);

    useImperativeHandle(ref, () => ({
      isEmpty: () =>
        editorRef.current?.unprivilegedEditor.getText().length <= 1,
    }));

    const focusHandler = () => {
      if (!readOnly) {
        setMaxLengthVisible(true);
        setFocus(true);
      }
    };

    const blurHandler = () => {
      if (!readOnly) {
        setMaxLengthVisible(false);
        setFocus(false);
      }
    };

    return (
      <div
        className={`editor-wrapper ${readOnly && "readOnly"} ${
          focus && "focused"
        } ${hasError && "ql-error"} ${customIcon && _value && "with-icon"} ${
          isCopilotField && "copilot-field"
        }`}
      >
        <InputLabel 
          className="text-editor-label" 
          required={required}
          shrink={!!_value || focus}
          variant="filled"
        >
          {label}
        </InputLabel>
        {customIcon && _value && (
          <div className="icon-container">{customIcon}</div>
        )}
        <ReactQuill
          onKeyDown={(e) => {
            if (e.keyCode == 8) return;
            if (
              maxLength &&
              editorRef.current?.unprivilegedEditor.getText().length >=
                maxLength
            ) {
              e.preventDefault();
              return false;
            }
          }}
          readOnly={readOnly}
          ref={editorRef}
          modules={modules}
          formats={formats}
          value={_value}
          onFocus={focusHandler}
          onBlur={blurHandler}
          placeholder=""
          data-placeholder={label}
          data-required={required}
          theme={theme}
          onChange={_setValue}
        />
        {maxLength &&
          maxLengthVisible &&
          MaxLengthValue(
            editorRef.current?.unprivilegedEditor.getText().length,
            maxLength
          )}
      </div>
    );
  }
);

SFTextEditor.propTypes = {
  defaultValue: PropTypes.node,
  onChange: PropTypes.func,
  label: PropTypes.string.isRequired,
  readOnly: PropTypes.bool,
  required: PropTypes.bool,
  height: PropTypes.number,
};

export default SFTextEditor;
